/* Optional one-PDP extension. All cart/pricing behavior stays in BundlePicker. */
window.JapBundleModelPicker = class {
  constructor(owner) {
    this.owner = owner;
    this.s = owner.data.modelStrings;
    this.catalog = owner.data.modelCatalog;
    this.catalog.forEach(({ key, product }) => {
      const existing = owner.family.find((item) => item.id === product.id);
      if (existing) existing.modelKey = key;
      else owner.family.push({ ...product, modelKey: key });
    });
    // Existing siblings missing from the collection retain the current family.
    const currentKey = owner.family[0].modelKey || String(owner.family[0].id);
    owner.family.forEach((item) => { item.modelKey ||= currentKey; });
    owner.classList.add('bundle-picker--models');
  }

  el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  button(text, className, action) {
    const button = this.el('button', className, text);
    button.type = 'button';
    button.addEventListener('click', action);
    return button;
  }

  colors(product) {
    return this.owner.family.filter((item) => item.modelKey === product.modelKey);
  }

  sizeLabel(title) {
    // Match the PDP's compact children's ranges without changing variant ids.
    const parts = String(title).trim().split('/');
    return parts.length > 1 && parts.every((part) => /^\d+$/.test(part))
      ? `${parts[0]}-${parts[parts.length - 1]}` : title;
  }

  totalOf(mode) {
    let cents = 0;
    let complete = true;
    for (const row of this.owner.rowsOf(mode)) {
      const variant = this.owner.variantOf(row);
      if (variant?.available) cents += variant.price;
      else {
        const prices = this.owner.family[row.product].variants.filter((v) => v.available).map((v) => v.price);
        if (!prices.length) return null;
        cents += Math.min(...prices);
        complete = false;
      }
    }
    return { cents, complete };
  }

  renderRow(row, index) {
    const owner = this.owner;
    const product = owner.family[row.product];
    const variant = owner.variantOf(row);
    const item = this.el('li', 'bundle-model-row');
    item.dataset.modelRow = index;
    if (product.image) item.appendChild(owner.thumb(product.image, product.title));
    const info = this.el('div', 'bundle-model-row__info');
    const heading = this.el('div', 'bundle-model-row__heading');
    heading.appendChild(this.el('span', 'bundle-model-row__number', owner.fill(owner.strings.row, { number: index + 1 })));
    info.appendChild(heading);
    info.appendChild(this.el('strong', 'bundle-model-row__name', product.title));
    if (row.main) {
      info.appendChild(this.el('span', 'bundle-model-row__summary', owner.fill(owner.strings.fromAbove, {
        size: variant ? `${product.color}, ${variant.title}` : product.color
      })));
    } else {
      const change = this.button(this.s.change, 'bundle-model-row__change', () => this.open(row, index, change));
      change.setAttribute('aria-haspopup', 'dialog');
      heading.appendChild(change);
      const controls = this.el('div', 'bundle-model-row__controls');
      const colors = this.colors(product);
      if (colors.length > 1) {
        const select = this.el('select', 'select');
        select.setAttribute('aria-label', `${owner.strings.color} ${index + 1}`);
        colors.forEach((color) => select.appendChild(new Option(color.color, String(owner.family.indexOf(color)), false, color.id === product.id)));
        select.addEventListener('change', () => {
          const previous = owner.variantOf(row);
          row.product = Number(select.value);
          row.variantId = owner.family[row.product].variants.find((v) => v.available && v.title === previous?.title)?.id || null;
          this.fillSizes(size, owner.family[row.product], row.variantId);
          const image = item.querySelector('img');
          if (image) image.src = owner.family[row.product].image || '';
          this.updateRow(item, row);
        });
        controls.appendChild(select);
      } else {
        controls.appendChild(this.el('span', 'bundle-model-row__color', product.color === product.title ? '' : product.color));
      }
      const size = this.el('select', 'select bundle-row__select');
      size.dataset.bundleSize = '';
      this.fillSizes(size, product, row.variantId);
      size.setAttribute('aria-label', `${owner.strings.size} ${index + 1}`);
      size.addEventListener('change', () => {
        row.variantId = Number(size.value) || null;
        this.updateRow(item, row);
      });
      controls.appendChild(size);
      info.appendChild(controls);
    }
    item.appendChild(info);
    const aside = this.el('div', 'bundle-model-row__aside');
    aside.appendChild(this.el('span', 'bundle-row__price', variant ? owner.money(variant.price) : ''));
    if (owner.mode === 'family' && index >= 2) {
      const remove = this.button('×', 'bundle-row__remove', () => {
        owner.rows.family.splice(index, 1);
        owner.render();
      });
      remove.setAttribute('aria-label', owner.strings.removePerson);
      aside.appendChild(remove);
    }
    item.appendChild(aside);
    return item;
  }

  fillSizes(select, product, variantId) {
    select.replaceChildren(new Option(this.owner.strings.sizePlaceholder, '', !variantId, !variantId));
    product.variants.forEach((variant) => {
      const label = this.sizeLabel(variant.title);
      const option = new Option(variant.available ? label : `${label} · ${this.owner.strings.soldOut}`, String(variant.id), false, variant.id === variantId);
      option.disabled = !variant.available;
      select.appendChild(option);
    });
  }

  updateRow(item, row) {
    const variant = this.owner.variantOf(row);
    item.querySelector('.bundle-row__price').textContent = variant ? this.owner.money(variant.price) : '';
    // Keep native select elements alive while their mobile picker is closing.
    this.owner.render({ preserveRows: true });
  }

  open(row, index, opener) {
    this.close();
    const owner = this.owner;
    this.row = row;
    this.rowIndex = index;
    this.mode = owner.mode;
    this.opener = opener;
    this.draft = { product: row.product, variantId: row.variantId };
    const dialog = this.el('dialog', 'bundle-model-dialog');
    this.dialog = dialog;
    const heading = this.el('div', 'bundle-model-dialog__heading');
    const title = this.el('h2', '', owner.fill(this.s.title, { number: index + 1 }));
    title.id = `${owner.dataset.formId}-model-title`;
    dialog.setAttribute('aria-labelledby', title.id);
    heading.appendChild(title);
    const close = this.button('×', 'bundle-model-dialog__close', () => this.close());
    close.setAttribute('aria-label', this.s.close);
    heading.appendChild(close);
    dialog.appendChild(heading);
    dialog.appendChild(this.el('p', 'bundle-model-dialog__intro', this.s.intro));
    const content = this.el('div', 'bundle-model-dialog__content');
    const grid = this.el('div', 'bundle-model-dialog__grid');
    const groups = new Map();
    owner.family.forEach((product) => {
      if (product.variants.some((v) => v.available) && !groups.has(product.modelKey)) groups.set(product.modelKey, product);
    });
    this.cards = [];
    groups.forEach((product) => {
      const card = this.button('', 'bundle-model-card', () => {
        if (owner.family[this.draft.product].modelKey !== product.modelKey) {
          this.draft = { product: owner.family.indexOf(product), variantId: null };
        }
        this.renderDetails();
        this.details.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        this.details.querySelector('select')?.focus({ preventScroll: true });
      });
      card.dataset.modelKey = product.modelKey;
      if (product.image) {
        const img = owner.thumb(product.image.replace(/width=120\b/, 'width=320'), product.title);
        img.loading = 'lazy';
        card.appendChild(img);
      }
      card.appendChild(this.el('strong', '', product.title));
      const variants = this.colors(product).flatMap((color) => color.variants).filter((v) => v.available);
      card.appendChild(this.el('span', '', owner.fill(owner.strings.fromTotal, { price: owner.money(Math.min(...variants.map((v) => v.price))) })));
      this.cards.push(card);
      grid.appendChild(card);
    });
    content.appendChild(grid);
    dialog.appendChild(content);
    this.details = this.el('div', 'bundle-model-dialog__details');
    dialog.appendChild(this.details);
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); });
    dialog.addEventListener('click', (event) => { if (event.target === dialog) {
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.close();
    } });
    document.body.appendChild(dialog);
    this.renderDetails();
    document.documentElement.classList.add('bundle-model-open');
    dialog.showModal();
    close.focus();
  }

  renderDetails() {
    const owner = this.owner;
    const product = owner.family[this.draft.product];
    this.cards.forEach((card) => card.setAttribute('aria-pressed', String(card.dataset.modelKey === product.modelKey)));
    this.details.textContent = '';
    this.details.appendChild(this.el('strong', 'bundle-model-dialog__selected', product.title));
    const controls = this.el('div', 'bundle-model-dialog__controls');
    const colors = this.colors(product);
    if (colors.length > 1) {
      const label = this.el('label', '', owner.strings.color);
      const select = this.el('select', 'select');
      colors.forEach((color) => select.appendChild(new Option(color.color, String(owner.family.indexOf(color)), false, color.id === product.id)));
      select.addEventListener('change', () => {
        // Reconfirm the size when replacing the product in the dialog.
        this.draft = { product: Number(select.value), variantId: null };
        this.fillSizes(size, owner.family[this.draft.product], null);
        this.updateConfirmation();
      });
      label.appendChild(select);
      controls.appendChild(label);
    } else if (product.color !== product.title) {
      controls.appendChild(this.el('span', '', `${owner.strings.color}: ${product.color}`));
    }
    const label = this.el('label', '', owner.strings.size);
    const size = this.el('select', 'select');
    size.dataset.modelSize = '';
    this.fillSizes(size, product, this.draft.variantId);
    size.addEventListener('change', () => {
      this.draft.variantId = Number(size.value) || null;
      this.updateConfirmation();
    });
    label.appendChild(size);
    controls.appendChild(label);
    this.details.appendChild(controls);
    const confirm = this.button('', 'btn btn--primary bundle-model-dialog__confirm', () => {
      const variant = owner.family[this.draft.product].variants.find((v) => v.id === this.draft.variantId && v.available);
      if (!variant || owner.mode !== this.mode || owner.currentRows()[this.rowIndex] !== this.row) return;
      Object.assign(this.row, this.draft);
      owner.render();
      this.close();
      owner.querySelector(`[data-model-row="${this.rowIndex}"] .bundle-model-row__change`)?.focus();
    });
    this.details.appendChild(confirm);
    this.updateConfirmation();
  }

  updateConfirmation() {
    const variant = this.owner.family[this.draft.product].variants.find((v) => v.id === this.draft.variantId && v.available);
    const confirm = this.details.querySelector('.bundle-model-dialog__confirm');
    confirm.textContent = this.owner.fill(this.s.confirm, { number: this.rowIndex + 1 }) + (variant ? ` · ${this.owner.money(variant.price)}` : '');
    confirm.disabled = !variant;
  }

  close() {
    if (!this.dialog) return;
    this.dialog.close();
    this.dialog.remove();
    this.dialog = null;
    document.documentElement.classList.remove('bundle-model-open');
    const opener = this.opener?.isConnected ? this.opener
      : this.owner.querySelector(`[data-bundle-option="${this.mode}"] [data-model-row="${this.rowIndex}"] .bundle-model-row__change`);
    opener?.focus();
  }
};
